import type { ColumnFiltersState } from "@tanstack/react-table";
import { z } from "zod";
import AppRoutes from "@/constants/routes/AppRoutes";
import { logger } from "@/lib/logger";
import { hasSearchContent, trimmedSearchTags } from "@/lib/search/storedSearch";
import { GlobalSearchTab, Search } from "@/models/search/Search";
import { DATASOURCE_FILTER_ID } from "@/pages/legislation/utils/datasourceFilter";
import { buildSearchUrlParams } from "@/utils/searchParamsFormatter";

/**
 * The Kilder table's search, in the shape the table state store keeps it.
 *
 * A source search is stored in exactly the same place as a global search — a
 * `searchUrl` on the user's search list — so that it shows up in recent
 * searches and can be favorited from there (LAR-422). The only differences are
 * the `sources` tab, which marks where the search has to be reopened, and the
 * path, which points at the Kilder page instead of the search modal.
 */
export interface SourceSearchTableState {
  search: string;
  searchTags: string[];
  columnFilters: ColumnFiltersState;
}

/** True for a stored search that has to be reopened on the Kilder page. */
export const isSourceSearch = (search: Search): boolean =>
  search.tab === GlobalSearchTab.Sources;

/**
 * Filter values are the options of the table's filter config, which are
 * strings (`FilterCfg.options`). Anything else cannot round trip through a URL
 * as itself, so it is left out rather than stringified into a value no filter
 * would match.
 */
const isStringValue = (value: unknown): value is string =>
  typeof value === "string";

/**
 * Column filters as `Search.filters`. An emptied filter keeps its key: the
 * table reads "no option selected" as "every option", so dropping the key
 * would silently widen the restored search.
 */
const toFilterRecord = (
  columnFilters: ColumnFiltersState
): Record<string, string[]> => {
  const filters: Record<string, string[]> = {};

  for (const { id, value } of columnFilters) {
    if (Array.isArray(value)) {
      filters[id] = value.filter(isStringValue);
    } else if (isStringValue(value)) {
      filters[id] = [value];
    }
  }

  return filters;
};

const filterValuesSchema = z.array(z.string());

/**
 * A stored filter back in the shape the table holds it in. Every filter is
 * stored as a list, but the Kilde is single-select: the table and its toolbar
 * keep the chosen source key itself. Restored as a one-item list, the toolbar
 * showed no Kilde, and with it none of the filters that depend on the Kilde,
 * such as Dokumenttype and Ministerium (LAR-553).
 */
const toColumnFilterValue = (
  id: string,
  values: string[]
): string | string[] =>
  id === DATASOURCE_FILTER_ID && values.length === 1 ? values[0] : values;

/**
 * A stored search's filters are untrusted: they reach us as JSON in a URL,
 * either from a link someone shares or from the search list, which stores
 * whatever `searchUrl` it was given. An entry that is not a list of strings is
 * dropped, which widens the restored search on that filter — hence the log.
 */
const toColumnFilters = (
  filters: Record<string, string[]> | undefined
): ColumnFiltersState => {
  const columnFilters: ColumnFiltersState = [];
  const droppedFilters: string[] = [];

  for (const [id, value] of Object.entries(filters ?? {})) {
    const parsed = filterValuesSchema.safeParse(value);
    if (parsed.success) {
      columnFilters.push({ id, value: toColumnFilterValue(id, parsed.data) });
    } else {
      droppedFilters.push(id);
    }
  }

  if (droppedFilters.length > 0) {
    logger.warn("Dropped unreadable filters from a stored source search", {
      operation: "toSourceSearchTableState",
      droppedFilters: droppedFilters.join(", "),
    });
  }

  return columnFilters;
};

/** The Kilder table's current search as a storable search. */
export const toSourceSearch = (state: SourceSearchTableState): Search => ({
  tab: GlobalSearchTab.Sources,
  searchTerm: state.search,
  searchTags: state.searchTags,
  filters: toFilterRecord(state.columnFilters),
});

/** The `searchUrl` a source search is stored and shared under. */
export const buildSourceSearchUrl = (state: SourceSearchTableState): string =>
  `${AppRoutes.legislations}?${buildSearchUrlParams(
    toSourceSearch(state)
  ).toString()}`;

/** A stored source search back as Kilder table state. */
export const toSourceSearchTableState = (
  search: Search
): SourceSearchTableState => ({
  search: search.searchTerm ?? "",
  searchTags: search.searchTags ?? [],
  columnFilters: toColumnFilters(search.filters),
});

/** Order-independent identity of a search, for spotting a changed search. */
const searchIdentity = (state: SourceSearchTableState): string =>
  JSON.stringify({
    search: state.search.trim(),
    searchTags: trimmedSearchTags(state.searchTags).sort(),
    filters: Object.entries(toFilterRecord(state.columnFilters))
      .map(([id, values]) => [id, [...values].sort()] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
  });

/**
 * Whether leaving the Kilder page should store the search. The search the
 * visit started with was stored when it was entered — the Kilder table keeps
 * its state in session storage, so re-saving it would add a duplicate recent
 * search for every visit that merely paged through results.
 */
export const shouldSaveSourceSearch = (
  visitStartedWith: SourceSearchTableState | null,
  current: SourceSearchTableState
): boolean => {
  if (!hasSearchContent(current.search, current.searchTags)) return false;
  if (!visitStartedWith) return true;

  return searchIdentity(visitStartedWith) !== searchIdentity(current);
};
